import { gql } from '@apollo/client';

// Category Queries
export const GET_CATEGORIES = gql`
  query GetCategories {
    categories {
      id
      code
      name
      description
      status
      createdDate
    }
  }
`;

export const GET_CATEGORY_BY_CODE = gql`
  query GetCategoryByCode($code: String!) {
    categoryByCode(code: $code) {
      id
      code
      name
      description
      status
      position
      createdDate
      lastModifiedDate
    }
  }
`;

// Item Queries
// Lightweight query — code/name/position/categoryCode only, no audio/image blobs
// Used to build default categoryOrder + itemOrder when creating a new configuration
export const GET_ITEMS_BASIC = gql`
  query GetItemsBasic {
    items {
      code
      name
      position
      categoryCode
      status
    }
  }
`;

export const GET_ITEMS = gql`
  query GetItems {
    items {
      id
      code
      name
      description
      categoryCode
      subCategoryCode
      status
      position
      associatedCategoryCodes
      imageData
      imageFileName
      imageGenerated
      createdDate
      updatedDate
      soundOptions {
        voiceId
        soundData
        soundFileName
        soundContentType
        soundGenerated
        soundGeneratedAt
      }
    }
  }
`;

export const GET_ITEMS_BY_CATEGORY = gql`
  query GetItemsByCategory($categoryCode: String!) {
    itemsByCategory(categoryCode: $categoryCode) {
      id
      code
      name
      description
      categoryCode
      subCategoryCode
      status
      position
      associatedCategoryCodes
      imageData
      imageFileName
      imageGenerated
      createdDate
      updatedDate
      soundOptions {
        voiceId
        soundData
        soundFileName
        soundContentType
        soundGenerated
        soundGeneratedAt
      }
    }
  }
`;

export const GET_ITEM_BY_CODE = gql`
  query GetItemByCode($code: String!) {
    itemByCode(code: $code) {
      id
      code
      name
      description
      categoryCode
      subCategoryCode
      status
      position
      associatedCategoryCodes
      imageData
      imageFileName
      imageGenerated
      createdDate
      updatedDate
      soundOptions {
        voiceId
        soundData
        soundFileName
        soundContentType
        soundGenerated
        soundGeneratedAt
      }
    }
  }
`;

// User/Security Queries
export const GET_USER_INFO = gql`
  query GetUserInfo {
    user {
      id
      displayName
      email
      roles
      roleId
      isActive
      lastLoginDate
      createdDate
    }
  }
`;

export const GET_SELF = gql`
  query GetSelf {
    getSelf {
      id
      username
      emailAddress
      firstName
      lastName
      displayName
      roleId
      isActive
      lastLoginDate
      createdDate
      notificationPreferences {
        enabled
        smsEnabled
        phoneNumber
        signalREnabled
        updatedDate
      }
      notificationGroups {
        userId
        status
      }
      mappings {
        tenantId
        roleId
      }
    }
  }
`;

// Security Management Queries
export const GET_SOURCES = gql`
  query GetSources {
    sources {
      id
      code
      name
      description
      status
      tenantId
      createdDate
      lastModifiedDate
    }
  }
`;

export const GET_SOURCE = gql`
  query GetSource($id: String!) {
    source(id: $id) {
      id
      code
      name
      description
      status
      tenantId
      createdDate
      lastModifiedDate
    }
  }
`;

export const GET_ROLES = gql`
  query GetRoles {
    roles {
      id
      roleId
      name
      description
      isActive
      createdDate
    }
  }
`;

export const GET_ROLE = gql`
  query GetRole($id: String!) {
    role(id: $id) {
      id
      roleId
      name
      description
      isActive
      createdDate
    }
  }
`;

export const GET_TENANTS = gql`
  query GetTenants {
    tenants {
      id
      name
      description
      isActive
      createdDate
    }
  }
`;

export const GET_TENANT = gql`
  query GetTenant($id: String!) {
    tenant(id: $id) {
      id
      name
      description
      isActive
      createdDate
    }
  }
`;

export const GET_USERS = gql`
  query GetUsers {
    users {
      id
      username
      emailAddress
      firstName
      lastName
      displayName
      languageCode
      identityProvider
      linkId
      roleId
      mappings {
        tenantId
        roleId
      }
      createdDate
      isActive
      lastLoginDate
      notificationPreferences {
        enabled
        smsEnabled
        phoneNumber
        signalREnabled
        updatedDate
      }
      notificationGroups {
        userId
        status
      }
    }
  }
`;

export const GET_FILTERED_USERS = gql`
  query GetFilteredUsers {
    filteredUsers {
      id
      username
      emailAddress
      firstName
      lastName
      displayName
      languageCode
      identityProvider
      linkId
      roleId
      mappings {
        tenantId
        roleId
      }
      createdDate
      isActive
      lastLoginDate
      notificationPreferences {
        enabled
        smsEnabled
        phoneNumber
        signalREnabled
        updatedDate
      }
      notificationGroups {
        userId
        status
      }
    }
  }
`;

export const GET_USER = gql`
  query GetUser($id: String!) {
    user(id: $id) {
      id
      username
      emailAddress
      firstName
      lastName
      displayName
      languageCode
      identityProvider
      linkId
      roleId
      mappings {
        tenantId
        roleId
      }
      createdDate
      isActive
      lastLoginDate
      notificationPreferences {
        enabled
        smsEnabled
        phoneNumber
        signalREnabled
        updatedDate
      }
      notificationGroups {
        userId
        status
      }
    }
  }
`;

export const GET_USER_BY_USERNAME = gql`
  query GetUserByUsername($username: String!) {
    userByUsername(username: $username) {
      id
      username
      email
      firstName
      lastName
      isActive
      roleId
      tenantId
      sourceId
      createdDate
      lastModifiedDate
      lastLogin
    }
  }
`;

// Voice Queries
export const GET_VOICES = gql`
  query GetVoices {
    voices {
      id
      voiceId
      name
      description
      isActive
      isDefault
      createdDate
    }
  }
`;

export const GET_AVAILABLE_VOICES = gql`
  query GetAvailableVoices {
    availableVoices {
      id
      name
      gender
      language
      provider
      isActive
    }
  }
`;

// System Status Queries
export const GET_OPENAI_STATUS = gql`
  query GetOpenAIStatus {
    openAIStatus {
      isAvailable
      lastChecked
      errorMessage
    }
  }
`;

export const GET_ELEVEN_LABS_STATUS = gql`
  query GetElevenLabsStatus {
    elevenLabsStatus {
      isAvailable
      lastChecked
      errorMessage
    }
  }
`;

// Image Queries
export const GET_SUPPORTED_IMAGE_SIZES = gql`
  query GetSupportedImageSizes {
    supportedImageSizes
  }
`;

// HierarchyLevel Queries
export const GET_HIERARCHY_LEVELS = gql`
  query GetHierarchyLevels {
    hierarchyLevels {
      id
      hierarchyLevelId
      name
      description
      createdDate
      isActive
      isSystemLevel
      isUserLevel
    }
  }
`;

export const GET_ACTIVE_HIERARCHY_LEVELS = gql`
  query GetActiveHierarchyLevels {
    activeHierarchyLevels {
      id
      hierarchyLevelId
      name
      description
      createdDate
      isActive
      isSystemLevel
      isUserLevel
    }
  }
`;

export const GET_HIERARCHY_LEVEL_BY_ID = gql`
  query GetHierarchyLevelById($id: String!) {
    hierarchyLevel(id: $id) {
      id
      hierarchyLevelId
      name
      description
      createdDate
      isActive
      isSystemLevel
      isUserLevel
    }
  }
`;

export const GET_NEXT_HIERARCHY_LEVEL_ID = gql`
  query GetNextHierarchyLevelId {
    nextHierarchyLevelId
  }
`;

// API Info
export const GET_API_INFO = gql`
  query GetApiInfo {
    apiVersion
    apiInfo
  }
`;

// User Customization Queries
export const GET_USER_CUSTOMIZATIONS_BY_TYPE = gql`
  query GetUserCustomizationsByType($customizationType: String!) {
    userCustomizationsByType(customizationType: $customizationType) {
      id
      userId
      label
      customizationType
      customData
      source
      sourceId
      isActive
      version
      createdDate
      updatedDate
    }
  }
`;

export const GET_USER_CUSTOMIZATIONS_FOR_USER = gql`
  query GetUserCustomizationsForUser($userId: String!) {
    userCustomizationsForUser(userId: $userId) {
      id
      userId
      label
      customizationType
      customData
      source
      sourceId
      isActive
      version
      createdDate
      updatedDate
    }
  }
`;

// Tenant Customization Queries (admin-only, roleId ≤ 2)
export const GET_TENANT_CUSTOMIZATIONS = gql`
  query GetTenantCustomizations($tenantId: String!) {
    tenantCustomizations(tenantId: $tenantId) {
      id
      tenantId
      label
      customizationType
      customData
      source
      sourceId
      isActive
      version
      createdDate
      updatedDate
    }
  }
`;

export const GET_TENANT_CUSTOMIZATIONS_BY_TYPE = gql`
  query GetTenantCustomizationsByType($tenantId: String!, $customizationType: String!) {
    tenantCustomizationsByType(tenantId: $tenantId, customizationType: $customizationType) {
      id
      tenantId
      label
      customizationType
      customData
      source
      sourceId
      isActive
      version
      createdDate
      updatedDate
    }
  }
`;
